import { useEffect, useState, useMemo } from "react";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useReveal } from "../hooks/useReveal";
import { waLink } from "../lib/wa";

export default function AttachmentsPage() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("All");
  const rootRef = useReveal([items.length, active]);

  useEffect(() => {
    api.get("/attachments").then(({ data }) => setItems(data));
  }, []);

  const categories = useMemo(
    () => ["All", ...new Set(items.map((i) => i.category))],
    [items]
  );

  const filtered = useMemo(
    () => (active === "All" ? items : items.filter((i) => i.category === active)),
    [items, active]
  );

  return (
    <div ref={rootRef} className="bg-white" data-testid="attachments-page">
      <Navbar />

      <section className="pt-36 md:pt-44 pb-12 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 reveal">
          <div className="overline">Genuine Attachments</div>
          <h1 className="h1-hero text-5xl md:text-7xl mt-4">
            One machine.
            <br />
            <span className="text-[#ff6a13]">A hundred tools.</span>
          </h1>
          <p className="mt-5 text-lg text-[#6b6b72] max-w-2xl">
            Turn your Bobcat into a breaker, auger, grader, mower, forklift or more.
            All the genuine attachments — stocked and serviced by Bhumi Equipments.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="sticky top-[72px] z-30 bg-white/95 backdrop-blur border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center gap-2 overflow-x-auto">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`att-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                active === c
                  ? "bg-[#111113] text-white"
                  : "bg-[#f5f5f7] text-[#111113] hover:bg-black/10"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((a) => (
            <div
              key={a.id}
              className="reveal group rounded-3xl overflow-hidden bg-[#f5f5f7] hover:bg-white border border-transparent hover:border-[#ff6a13]/30 hover:shadow-xl transition-all"
              data-testid={`attachment-card-${a.id}`}
            >
              <div className="aspect-[4/3] overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#f5f5f7] to-[#ecedf0]">
                <img src={a.image} alt={a.name} className="w-[80%] h-auto group-hover:scale-105 transition-transform duration-700 no-drag" />
              </div>
              <div className="p-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#ff6a13] font-bold">{a.category}</div>
                <div className="mt-2 font-display text-xl font-bold text-[#111113]">{a.name}</div>
                <div className="mt-2 text-sm text-[#6b6b72]">{a.short_desc}</div>
                <a
                  href={waLink({ source: `attachment_${a.id}`, message: `Hi, I'd like to enquire about the ${a.name} attachment.` })}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#111113] group-hover:text-[#ff6a13]"
                  data-testid={`att-enquire-${a.id}`}
                >
                  <MessageCircle size={14} /> Enquire on WhatsApp
                </a>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full py-20 text-center text-[#6b6b72]">No attachments to show.</div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#111113] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="overline text-[#ff6a13]">Can't find what you need?</div>
          <h3 className="h2-section text-3xl md:text-5xl mt-4">We probably have it.</h3>
          <p className="mt-5 text-white/60 max-w-xl mx-auto">
            Bobcat makes 80+ attachments across loaders, excavators, and backhoes. Send us
            your use-case — we'll recommend the exact attachment + machine combo.
          </p>
          <a
            href={waLink({ source: "attachments_cta", message: "Hi Bhumi Equipments, I need a custom attachment recommendation." })}
            target="_blank"
            rel="noreferrer"
            className="mt-8 btn-primary"
            data-testid="attachments-cta"
          >
            <MessageCircle size={18} /> Ask on WhatsApp <ArrowUpRight size={16} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
